import { useMemo } from "react";
import { Banknote, Repeat, TrendingDown, TrendingUp } from "lucide-react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { SafeChart } from "@/components/SafeChart";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  formatInvestorMoney,
  type MrrEvolutionMonthRow,
  type MrrEvolutionPeriodRow,
} from "@/lib/investorKpiDisplay";
import { cn } from "@/lib/utils";

function formatUsdAxis(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}k`;
  return `$${Math.round(value)}`;
}

function formatMonthLabel(ym: string): string {
  const [y, m] = ym.split("-");
  const months = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  const idx = Number(m) - 1;
  return idx >= 0 && idx < 12 ? `${months[idx]} ${y?.slice(2) ?? ""}` : ym;
}

function PctBadge({ pct }: { pct: number | null }) {
  if (pct === null) return <span className="text-[10px] text-muted-foreground">base</span>;
  const up = pct >= 0;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-[10px] font-semibold tabular-nums",
        up ? "text-emerald-600" : "text-rose-600",
      )}
    >
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {up ? "+" : ""}
      {pct}%
    </span>
  );
}

type Props = {
  /** MRR a cobrar (run-rate APIs). */
  totalCombinedUsd: number;
  stripeMrrUsd: number;
  mrrArs: number;
  /** Recurrente ya cobrado en el mes actual (USD equiv.). */
  collectedMtdUsd: number;
  exchangeRate: number;
  activeSubscriptions: number;
  evolutionMonthly: MrrEvolutionMonthRow[];
  evolutionByPeriod: MrrEvolutionPeriodRow[];
};

export function InvestorMrrEvolutionCharts({
  totalCombinedUsd,
  stripeMrrUsd,
  mrrArs,
  collectedMtdUsd,
  exchangeRate,
  activeSubscriptions,
  evolutionMonthly,
  evolutionByPeriod,
}: Props) {
  const monthlyChart = useMemo(
    () =>
      evolutionMonthly.map((m) => ({
        ...m,
        label: formatMonthLabel(m.month),
        collectedUsd: m.combined_usd,
        runRateUsd: m.run_rate_combined_usd ?? (m.is_api_snapshot ? m.combined_usd : null),
      })),
    [evolutionMonthly],
  );

  const periodChart = useMemo(
    () =>
      evolutionByPeriod.map((p) => ({
        ...p,
        label: `P${p.period_id}`,
        runRateUsd: p.mrr_run_rate_combined_usd,
        collectedUsd: p.combined_usd,
      })),
    [evolutionByPeriod],
  );

  const hasMonthly = monthlyChart.some((m) => m.collectedUsd > 0 || (m.runRateUsd ?? 0) > 0);
  const hasPeriod = periodChart.some((p) => p.collectedUsd > 0 || (p.runRateUsd ?? 0) > 0);

  if (!hasMonthly && !hasPeriod) return null;

  const currentMonth = monthlyChart[monthlyChart.length - 1];
  const prevMonth = monthlyChart.length > 1 ? monthlyChart[monthlyChart.length - 2] : null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="glass-card border-violet-500/30 bg-gradient-to-br from-violet-500/10 via-transparent to-transparent shadow-sm">
          <CardContent className="pt-6 pb-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-violet-600 dark:text-violet-400 flex items-center gap-1.5">
                  <Repeat className="h-3.5 w-3.5" />
                  MRR a cobrar (próximo ciclo)
                </p>
                <p className="font-display text-4xl font-bold tabular-nums text-foreground mt-1">
                  {formatInvestorMoney(totalCombinedUsd, "USD")}
                </p>
                <p className="text-sm text-muted-foreground mt-2 tabular-nums">
                  {formatInvestorMoney(stripeMrrUsd, "USD")} Stripe + {formatInvestorMoney(mrrArs, "ARS")} MP
                  <span className="mx-1.5 text-border">·</span>
                  ARS÷{exchangeRate.toLocaleString("es-AR")}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {activeSubscriptions} subs (Stripe scope anaafernandez + MP preapproval)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-transparent to-transparent shadow-sm">
          <CardContent className="pt-6 pb-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                  <Banknote className="h-3.5 w-3.5" />
                  Recurrente cobrado (mes actual)
                </p>
                <p className="font-display text-4xl font-bold tabular-nums text-foreground mt-1">
                  {formatInvestorMoney(collectedMtdUsd, "USD")}
                </p>
                <p className="text-[11px] text-muted-foreground mt-2">
                  Cobrado recurrente: stripe_transactions + manual_transactions (scope marca)
                </p>
              </div>
              {prevMonth && currentMonth?.pct_change_combined !== null && (
                <div className="rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3 text-right backdrop-blur-sm">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">cobrado vs mes anterior</p>
                  <p className="text-2xl font-bold mt-0.5">
                    <PctBadge pct={currentMonth.pct_change_combined} />
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {hasMonthly && (
        <Card className="glass-card border-border/60 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Repeat className="h-4 w-4 text-emerald-500" />
              Evolución mensual — cobrado real (USD total)
            </CardTitle>
            <CardDescription className="text-[11px]">
              Barras = recurrente ya cobrado · línea violeta = MRR a cobrar (API, solo mes actual)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SafeChart>
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={monthlyChart} margin={{ top: 8, right: 16, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis
                    tickFormatter={formatUsdAxis}
                    tick={{ fontSize: 11 }}
                    width={52}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.[0]) return null;
                      const row = payload[0].payload as (typeof monthlyChart)[number];
                      return (
                        <div className="min-w-[240px] rounded-lg border border-white/10 bg-black/90 p-3 text-xs shadow-xl backdrop-blur-sm">
                          <p className="font-semibold mb-1">{row.label}</p>
                          <p className="text-emerald-700 font-medium">
                            Cobrado: {formatInvestorMoney(row.collectedUsd, "USD")}
                          </p>
                          <p className="text-muted-foreground">
                            Stripe {formatInvestorMoney(row.stripe_recurring_usd, "USD")} · MP{" "}
                            {formatInvestorMoney(row.mercadopago_recurring_ars, "ARS")}
                          </p>
                          {row.runRateUsd != null && (
                            <p className="text-violet-600 font-medium mt-1">
                              MRR a cobrar (API): {formatInvestorMoney(row.runRateUsd, "USD")}
                              <Badge variant="outline" className="ml-2 h-4 text-[9px]">
                                proyectado
                              </Badge>
                            </p>
                          )}
                          {row.pct_change_combined !== null && (
                            <p className="mt-1">
                              cobrado vs mes ant.: <PctBadge pct={row.pct_change_combined} />
                            </p>
                          )}
                        </div>
                      );
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar
                    dataKey="collectedUsd"
                    fill="hsl(142 76% 36% / 0.35)"
                    name="Cobrado recurrente"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                  />
                  <Line
                    type="monotone"
                    dataKey="runRateUsd"
                    stroke="#7c3aed"
                    strokeWidth={2.5}
                    dot={{ r: 5, fill: "#7c3aed" }}
                    connectNulls={false}
                    name="MRR a cobrar (API)"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </SafeChart>
          </CardContent>
        </Card>
      )}

      {hasPeriod && (
        <Card className="glass-card border-border/60 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Evolución por período (P1–P5)</CardTitle>
            <CardDescription className="text-[11px]">
              Barras = recurrente cobrado en el corte · línea ámbar = suscripciones activas al cierre · violeta = MRR API (período actual)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SafeChart>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={periodChart} margin={{ top: 8, right: 20, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fontWeight: 600 }} tickLine={false} axisLine={false} />
                  <YAxis
                    yAxisId="usd"
                    tickFormatter={formatUsdAxis}
                    tick={{ fontSize: 11 }}
                    width={48}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    yAxisId="subs"
                    orientation="right"
                    tick={{ fontSize: 11 }}
                    width={36}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.[0]) return null;
                      const row = payload[0].payload as (typeof periodChart)[number];
                      return (
                        <div className="min-w-[200px] rounded-lg border border-white/10 bg-black/90 p-3 text-xs shadow-xl backdrop-blur-sm">
                          <p className="font-semibold">Período {row.period_id}</p>
                          <p className="text-muted-foreground">
                            {row.from} → {row.to}
                          </p>
                          <p className="mt-1 text-emerald-700 font-medium">
                            Cobrado recurrente: {formatInvestorMoney(row.collectedUsd, "USD")}
                          </p>
                          {row.runRateUsd != null && (
                            <p className="text-violet-600 font-medium">
                              MRR a cobrar (API): {formatInvestorMoney(row.runRateUsd, "USD")}
                            </p>
                          )}
                          <p>Activas cierre: {row.active_at_end}</p>
                          <p className="text-emerald-600">+{row.new_subscriptions} altas</p>
                          <p className="text-rose-600">
                            −{row.lost_from_previous_period ?? row.cancelled_in_period} bajas
                          </p>
                          {row.pct_change_combined !== null && (
                            <p className="mt-1">
                              cobrado vs período ant.: <PctBadge pct={row.pct_change_combined} />
                            </p>
                          )}
                        </div>
                      );
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar
                    yAxisId="usd"
                    dataKey="collectedUsd"
                    fill="hsl(142 76% 36% / 0.3)"
                    name="Recurrente cobrado"
                    maxBarSize={48}
                    radius={[4, 4, 0, 0]}
                  />
                  <Line
                    yAxisId="usd"
                    type="monotone"
                    dataKey="runRateUsd"
                    stroke="#7c3aed"
                    strokeWidth={2.5}
                    strokeDasharray="4 3"
                    dot={{ r: 4, fill: "#7c3aed" }}
                    connectNulls={false}
                    name="MRR a cobrar (API)"
                  />
                  <Line
                    yAxisId="subs"
                    type="monotone"
                    dataKey="active_at_end"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    name="Activas cierre"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </SafeChart>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2">
              {periodChart.map((p) => (
                <div key={p.period_id} className="rounded-md border border-border/40 bg-muted/20 px-2 py-2 text-center">
                  <p className="text-[10px] font-bold">P{p.period_id}</p>
                  <p className="text-[10px] font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">
                    {formatInvestorMoney(p.collectedUsd, "USD")}
                  </p>
                  <PctBadge pct={p.pct_change_combined} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}